import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

interface BrandLogoProps {
  size?: number;
  showWordmark?: boolean;
  stacked?: boolean;
  textColor?: string;
  accentColor?: string;
  subtitle?: string;
  sx?: SxProps<Theme>;
}

const BrandLogo: React.FC<BrandLogoProps> = ({
  size = 56,
  showWordmark = true,
  stacked = false,
  textColor = 'inherit',
  accentColor,
  subtitle,
  sx,
}) => {
  const theme = useTheme();
  // Follow the active palette (ocean teal / magenta) unless explicitly overridden
  const resolvedAccent = accentColor
    ?? (theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main);
  // Ocean palettes have a blue-dominant primary (#0097A7/#006978) vs magenta's
  // red-dominant (#D4186A/#A0104C) — pick the matching recolor of the same logo
  const primaryHex = theme.palette.primary.main;
  const isOceanPalette = parseInt(primaryHex.slice(5, 7), 16) >= parseInt(primaryHex.slice(1, 3), 16);
  const logoSrc = isOceanPalette ? '/nas-logo-ocean.jpg' : '/nas-logo.jpg';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: stacked ? 1.25 : 1.5,
        flexDirection: stacked ? 'column' : 'row',
        minWidth: 0,
        ...sx,
      }}
    >
      <Box
        component="img"
        src={logoSrc}
        alt="New Achievement Scuba logo"
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          display: 'block',
          borderRadius: '12px',
          objectFit: 'cover',
          filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.4))',
        }}
      />

      {showWordmark ? (
        <Box sx={{ minWidth: 0, textAlign: stacked ? 'center' : 'left' }}>
          <Typography
            component="div"
            sx={{
              color: textColor,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: '0.03em',
              fontSize: stacked ? { xs: '1.45rem', sm: '1.7rem' } : { xs: '1rem', sm: '1.25rem' },
            }}
          >
            New Achievement
            <Box component="span" sx={{ color: resolvedAccent, display: 'block', fontSize: '0.85em' }}>
              Scuba
            </Box>
          </Typography>
          {subtitle ? (
            <Typography
              component="div"
              sx={{
                color: textColor,
                opacity: 0.78,
                mt: 0.45,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontSize: stacked ? '0.65rem' : '0.6rem',
                whiteSpace: 'nowrap',
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export default BrandLogo;
